import { Component, Input, Output, EventEmitter, OnChanges, OnInit, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, ViewChild, ElementRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ConciliadoraService } from '../../conciliadora/services/conciliadora.service';
import { ConciliadoraTipoConfig, TipoDpara, MktpCfgOption } from '../../conciliadora/models/conciliadora.models';

@Component({
  standalone: false,
  selector: 'app-config-tipos',
  templateUrl: './config-tipos.component.html',
  styleUrls: ['./config-tipos.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfigTiposComponent implements OnChanges, OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() nuconf!: number;
  @Input() configsMktpCfg: MktpCfgOption[] = [];
  @Output() tiposLoaded = new EventEmitter<number>();

  tipos: ConciliadoraTipoConfig[] = [];
  loading = false;

  editingTipo: Partial<ConciliadoraTipoConfig> | null = null;
  tipoForm!: FormGroup;
  parametros: TipoDpara[] = [];

  private loadTipos$ = new ReplaySubject<void>(1);

  constructor(
    private svc: ConciliadoraService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private msg: NzMessageService,
    private modal: NzModalService
  ) {
    this.tipoForm = this.fb.group({
      IDTIPO: [null],
      TIPOS: ['', Validators.required],
      DESCRICAO: [''],
      IDCONF: [null],
      ORDEM: [null],
    });
  }

  ngOnInit(): void {
    this.loadTipos$
      .pipe(
        switchMap(() => {
          if (!this.nuconf) {
            return of<ConciliadoraTipoConfig[]>([]);
          }
          this.loading = true;
          this.cdr.markForCheck();
          return this.svc.getTiposByConfig(this.nuconf).pipe(
            catchError(() => {
              this.msg.error('Erro ao carregar tipos');
              return of<ConciliadoraTipoConfig[]>([]);
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data) => {
        this.tipos = data;
        this.loading = false;
        this.tiposLoaded.emit(this.tipos.length);
        this.cdr.markForCheck();
      });

    this.tipoForm
      .get('IDCONF')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((idconf) => this.warnIfNewMode(idconf));
  }

  /** Mostra modal explicativo na primeira selecao de fluxo NEWMODE='S' (uma vez por usuario). */
  private warnIfNewMode(idconf: number | null | undefined): void {
    if (!idconf) return;
    const cfg = this.configsMktpCfg.find((c) => c.IDCONF === idconf);
    if (!cfg?.newMode) return;
    const FLAG = 'conciliadora.newmode.warned';
    if (typeof localStorage !== 'undefined' && localStorage.getItem(FLAG) === '1') return;

    this.modal.confirm({
      nzTitle: 'Fluxo do motor novo (NEWMODE)',
      nzContent:
        'Este fluxo foi criado no motor novo do construtor de telas (NEWMODE=\'S\'). ' +
        'A Conciliadora vai chama-lo via /FlowServices/runOnline e injetar __rows, __metadados e __contextoAcao. ' +
        'Confirme se o fluxo esta preparado para ler essas variaveis. Esta mensagem nao aparecera novamente.',
      nzOkText: 'Entendi',
      nzCancelText: 'Trocar de fluxo',
      nzOnOk: () => {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(FLAG, '1');
        }
      },
      nzOnCancel: () => {
        this.tipoForm.get('IDCONF')!.setValue(null, { emitEvent: false });
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['nuconf'] && this.nuconf) {
      this.loadTipos();
    }
  }

  loadTipos(): void {
    this.loadTipos$.next();
  }

  // ========== PARAMETROS DPARA ==========

  parseParametros(json: string | null): TipoDpara[] {
    if (!json) return [];
    try {
      return JSON.parse(json) as TipoDpara[];
    } catch {
      return [];
    }
  }

  onAddParametro(): void {
    this.parametros = [...this.parametros, { campo: '', valor: '' }];
    this.cdr.markForCheck();
  }

  onRemoveParametro(index: number): void {
    this.parametros = this.parametros.filter((_, i) => i !== index);
    this.cdr.markForCheck();
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ========== CRUD ==========

  onAddTipo(): void {
    this.editingTipo = { NUCONF: this.nuconf };
    this.tipoForm.reset();
    this.parametros = [];
    this.cdr.markForCheck();
  }

  @ViewChild('editForm') editFormEl?: ElementRef<HTMLElement>;

  onEditTipo(tipo: ConciliadoraTipoConfig): void {
    this.editingTipo = tipo;
    this.tipoForm.patchValue(tipo);
    this.parametros = this.parseParametros(tipo.PARAMETROS);
    this.cdr.markForCheck();
    // O form de edicao aparece no topo; rola ate ele pra deixar claro que abriu
    // (antes o usuario, posicionado num tipo la embaixo, nao via o form abrir).
    setTimeout(() => this.editFormEl?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }

  onCancelTipo(): void {
    this.editingTipo = null;
    this.tipoForm.reset();
    this.parametros = [];
    this.cdr.markForCheck();
  }

  onSaveTipo(): void {
    if (!this.tipoForm.valid) {
      Object.values(this.tipoForm.controls).forEach((c) => {
        c.markAsDirty();
        c.updateValueAndValidity();
      });
      this.msg.warning('Preencha os campos obrigatórios');
      return;
    }

    const validParams = this.parametros.filter((p) => p.campo.trim() !== '');

    this.loading = true;
    const data = {
      ...this.tipoForm.getRawValue(),
      NUCONF: this.nuconf,
      PARAMETROS: validParams.length ? JSON.stringify(validParams) : null,
    };
    this.svc
      .saveTipo(data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.msg.success('Tipo salvo com sucesso');
          this.editingTipo = null;
          this.parametros = [];
          this.loadTipos();
        },
        error: () => {
          this.loading = false;
          this.msg.error('Erro ao salvar tipo');
          this.cdr.markForCheck();
        },
      });
  }

  onDeleteTipo(tipo: ConciliadoraTipoConfig): void {
    this.modal.confirm({
      nzTitle: 'Excluir Tipo',
      nzContent: `Excluir tipo "${tipo.TIPOS}"?`,
      nzOkDanger: true,
      nzOkText: 'Excluir',
      nzCancelText: 'Cancelar',
      nzOnOk: () => {
        this.svc
          .deleteTipo(tipo.IDTIPO)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.msg.success('Tipo excluído');
              this.loadTipos();
            },
            error: () => this.msg.error('Erro ao excluir tipo'),
          });
      },
    });
  }

  // ========== HELPERS ==========

  getConfigName(idconf: number | null): string {
    if (!idconf) return '—';
    const f = this.configsMktpCfg.find((c) => c.IDCONF === idconf);
    return f ? f.NAME : `#${idconf}`;
  }

  getParamCount(tipo: ConciliadoraTipoConfig): number {
    return this.parseParametros(tipo.PARAMETROS).length;
  }
}
